import { PRO_PRICE_PER_MONTH } from "~/utils/pricing";
import { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import * as DocumentPicker from "expo-document-picker";
import { useForm } from "react-hook-form";
import { useLocalSearchParams, useRouter } from "expo-router";
import { Alert, Image, Linking, View } from "react-native";
import { z } from "zod";

import { DeleteAccountPanel } from "~/components/composed/DeleteAccountPanel";
import { FormField } from "~/components/composed/FormField";
import {
  AppText,
  Button,
  ButtonText,
  Card,
  EmptyState,
  PasswordField,
  ProtectedScreen,
  Row,
  SearchableSelect,
  SegmentedControl,
  SectionHeader,
  Stack,
  TextField,
  ThemeToggle,
} from "~/components/ui";
import {
  checkoutSortValue,
  projectStateOptions,
  ProjectStatusEditor,
  purchasePackageLabel,
  RatePresetEditor,
  signupPlanLabel,
  StatusColourPicker,
  makeStylesheet,
  type ProjectStatus,
  type ProjectState,
  type RatePreset,
} from "~/features/settings";
import { AppVersionPanel } from "~/lib/app-updates";
import { useTheme, useThemedStyles } from "~/styles/theme";
import { useAccountPrivacy } from "~/hooks/useAccountPrivacy";
import { useAuth } from "~/hooks/useAuth";
import { usePurchases } from "~/hooks/usePurchases";
import {
  useSubscription,
  useWorkspaceSubscription,
} from "~/hooks/useSubscription";
import { TwoFactorCard } from "~/components/composed/TwoFactorCard";
import {
  usePrimaryWorkspace,
  useWorkspaceClients,
  useWorkspaceMembership,
  useWorkspaceProjects,
  useWorkspaceProjectStatuses,
  useWorkspaceSettings,
  useWorkspaceUserRates,
} from "~/hooks/workspace";
import {
  changePasswordSchema,
  passwordSchema,
  passwordResetSchema,
  profileSchema,
} from "~/utils/form-schemas";
import {
  CURRENCY_OPTIONS,
  LOCALE_OPTIONS,
  TIMEZONE_OPTIONS,
} from "~/utils/regional-options";
import {
  clearPendingCheckoutIntent,
  normalizeCheckoutIntent,
} from "~/utils/signup-intent";
import {
  INVOICE_ROUNDING_OPTIONS,
  type InvoiceRoundingMethod,
} from "~/utils/invoice-rounding";
import { removeFile, signedFileUrl, uploadFile } from "~/api/uploads";
import { db } from "~/api/db";
import {
  TIME_ROUNDING_OPTIONS,
  type TimeRoundingMethod,
  workspaceLogoPublicUrl,
} from "~/utils/time-rounding";
import { SvgXml } from "react-native-svg";

type ProfileValues = z.infer<typeof profileSchema>;
type ChangePasswordValues = z.infer<typeof changePasswordSchema>;
type SetPasswordValues = z.infer<typeof passwordResetSchema>;
type PasswordValues = z.infer<typeof passwordSchema>;
export default function SettingsScreen() {
  const stylesheet = useThemedStyles(makeStylesheet);
  const router = useRouter();
  const params = useLocalSearchParams<{
    checkout?: string;
    billing?: string;
  }>();
  const {
    user,
    refresh,
    updateProfile,
    updateProfilePending,
    changePassword,
    changePasswordPending,
    setPassword,
    setPasswordPending,
    logout,
    logoutPending,
  } = useAuth();
  const subscription = useSubscription();
  const purchases = usePurchases(user?.id ?? null);
  const checkoutIntent = normalizeCheckoutIntent(params);
  const privacy = useAccountPrivacy();
  const { colors } = useTheme();
  const workspace = usePrimaryWorkspace();
  const workspaceSubscription = useWorkspaceSubscription(workspace.data?.id);
  const workspaceSettings = useWorkspaceSettings(workspace.data?.id);
  const workspaceMembership = useWorkspaceMembership(workspace.data?.id);
  const clients = useWorkspaceClients(workspace.data?.id);
  const projects = useWorkspaceProjects(workspace.data?.id);
  const projectStatuses = useWorkspaceProjectStatuses(workspace.data?.id);
  const userRates = useWorkspaceUserRates(workspace.data?.id);
  const [workspaceName, setWorkspaceName] = useState("");
  const [legalName, setLegalName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [nzbn, setNzbn] = useState("");
  const [gstNumber, setGstNumber] = useState("");
  const [irdNumber, setIrdNumber] = useState("");
  const [gstRegistered, setGstRegistered] = useState(false);
  const [bankName, setBankName] = useState("");
  const [bankAccountName, setBankAccountName] = useState("");
  const [bankAccountNumber, setBankAccountNumber] = useState("");
  const [defaultPaymentInstructions, setDefaultPaymentInstructions] =
    useState("");
  const [invoicePrefix, setInvoicePrefix] = useState("MS");
  const [defaultInvoiceRounding, setDefaultInvoiceRounding] =
    useState<InvoiceRoundingMethod>("none");
  const [defaultTimeRounding, setDefaultTimeRounding] =
    useState<TimeRoundingMethod>("none");
  const [brandAccentHex, setBrandAccentHex] = useState("");
  const [logoPath, setLogoPath] = useState<string | null>(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [defaultHourlyRate, setDefaultHourlyRate] = useState("0");
  const [taxSetAsidePercentage, setTaxSetAsidePercentage] = useState("0");
  const [idleNudgeMinutes, setIdleNudgeMinutes] = useState("15");
  const [focusReminderMinutes, setFocusReminderMinutes] = useState("0");
  const [invoiceEmailSubjectTemplate, setInvoiceEmailSubjectTemplate] =
    useState("");
  const [invoiceEmailBodyTemplate, setInvoiceEmailBodyTemplate] = useState("");
  const [quoteEmailSubjectTemplate, setQuoteEmailSubjectTemplate] =
    useState("");
  const [quoteEmailBodyTemplate, setQuoteEmailBodyTemplate] = useState("");
  const [newStatusName, setNewStatusName] = useState("");
  const [newStatusColour, setNewStatusColour] = useState("#0F766E");
  const [newStatusState, setNewStatusState] = useState<ProjectState>("active");
  const [newRateName, setNewRateName] = useState("");
  const [newRateDescription, setNewRateDescription] = useState("");
  const [newRateHourly, setNewRateHourly] = useState("");
  const [newRateEffectiveFrom, setNewRateEffectiveFrom] = useState(
    new Date().toISOString().slice(0, 10),
  );
  const [newRateClientId, setNewRateClientId] = useState("");
  const [newRateProjectId, setNewRateProjectId] = useState("");
  const profileForm = useForm<ProfileValues>({
    resolver: zodResolver(profileSchema),
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: {
      firstName: "",
      lastName: "",
      currency: "NZD",
      dateFormat: "en-NZ",
      timezone: "Pacific/Auckland",
    },
  });
  const passwordForm = useForm<ChangePasswordValues>({
    resolver: zodResolver(changePasswordSchema),
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: { currentPassword: "", newPassword: "" },
  });
  const setPasswordForm = useForm<SetPasswordValues>({
    resolver: zodResolver(passwordResetSchema),
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: { password: "", confirmPassword: "" },
  });

  useEffect(() => {
    if (!user) return;
    profileForm.reset({
      firstName: user.firstName,
      lastName: user.lastName,
      currency: user.currency,
      dateFormat: user.dateFormat,
      timezone: user.timezone,
    });
  }, [profileForm, user]);

  useEffect(() => {
    if (!workspace.data) return;
    setWorkspaceName(workspace.data.name);
    setLegalName(workspace.data.legal_name ?? "");
    setContactEmail(workspace.data.contact_email ?? "");
    setPhone(workspace.data.phone ?? "");
    setAddress(workspace.data.address ?? "");
    setNzbn(workspace.data.nzbn ?? "");
    setGstNumber(workspace.data.gst_number ?? "");
    setIrdNumber(workspace.data.ird_number ?? "");
    setGstRegistered(workspace.data.gst_registered);
    setBankName(workspace.data.bank_name ?? "");
    setBankAccountName(workspace.data.bank_account_name ?? "");
    setBankAccountNumber(workspace.data.bank_account_number ?? "");
    setDefaultPaymentInstructions(
      workspace.data.default_payment_instructions ?? "",
    );
    setInvoicePrefix(workspace.data.invoice_prefix);
    setDefaultInvoiceRounding(workspace.data.default_invoice_rounding);
    setDefaultTimeRounding(workspace.data.default_time_rounding ?? "none");
    setBrandAccentHex(workspace.data.brand_accent_hex ?? "");
    setLogoPath(workspace.data.logo_path);
    setDefaultHourlyRate(
      String(workspace.data.default_hourly_rate_cents / 100),
    );
    setTaxSetAsidePercentage(String(workspace.data.tax_set_aside_percentage));
    setIdleNudgeMinutes(String(workspace.data.idle_nudge_minutes ?? 15));
    setFocusReminderMinutes(String(workspace.data.focus_reminder_minutes ?? 0));
    setInvoiceEmailSubjectTemplate(
      workspace.data.invoice_email_subject_template ?? "",
    );
    setInvoiceEmailBodyTemplate(
      workspace.data.invoice_email_body_template ?? "",
    );
    setQuoteEmailSubjectTemplate(
      workspace.data.quote_email_subject_template ?? "",
    );
    setQuoteEmailBodyTemplate(workspace.data.quote_email_body_template ?? "");
  }, [workspace.data]);

  const submitProfile = profileForm.handleSubmit(async (values) => {
    try {
      await updateProfile({
        ...values,
        currency: values.currency.toUpperCase(),
      });
      Alert.alert("Profile updated");
    } catch (error) {
      profileForm.setError("root", {
        message:
          error instanceof Error ? error.message : "Unable to update profile.",
      });
    }
  });
  const saveWorkspaceSettings = async () => {
    try {
      await workspaceSettings.mutateAsync({
        address,
        bankAccountName,
        bankAccountNumber,
        bankName,
        brandAccentHex,
        contactEmail,
        defaultHourlyRate,
        defaultInvoiceRounding,
        defaultPaymentInstructions,
        defaultTimeRounding,
        focusReminderMinutes,
        gstNumber,
        gstRegistered,
        idleNudgeMinutes,
        irdNumber,
        invoiceEmailBodyTemplate,
        invoiceEmailSubjectTemplate,
        invoicePrefix,
        legalName,
        logoPath: logoPath ?? "",
        name: workspaceName,
        nzbn,
        phone,
        quoteEmailBodyTemplate,
        quoteEmailSubjectTemplate,
        taxSetAsidePercentage: workspaceSubscription.canUseConfigurableSetAside
          ? taxSetAsidePercentage
          : String(workspace.data?.tax_set_aside_percentage ?? 0),
      });
      Alert.alert("Workspace updated");
    } catch (error) {
      Alert.alert(
        "Workspace update failed",
        error instanceof Error ? error.message : "Unable to update workspace.",
      );
    }
  };

  async function pickAndUploadLogo() {
    if (!workspace.data?.id || !user) return;
    const result = await DocumentPicker.getDocumentAsync({
      type: ["image/jpeg", "image/png", "image/webp"],
      copyToCacheDirectory: true,
    });
    if (result.canceled) return;
    const asset = result.assets[0];
    if (!asset) return;
    if (asset.size && asset.size > 2 * 1024 * 1024) {
      Alert.alert("Logo too large", "Choose a JPEG, PNG, or WebP up to 2 MB.");
      return;
    }
    const mimeType = asset.mimeType ?? "";
    const extension =
      mimeType === "image/png"
        ? "png"
        : mimeType === "image/webp"
          ? "webp"
          : mimeType === "image/jpeg"
            ? "jpg"
            : null;
    if (!extension) {
      Alert.alert("Unsupported file", "Logo must be a JPEG, PNG, or WebP.");
      return;
    }
    setLogoUploading(true);
    try {
      const fileBody = asset.file
        ? await asset.file.arrayBuffer()
        : await (await fetch(asset.uri)).arrayBuffer();
      const { path } = await uploadFile({
        workspaceId: workspace.data.id,
        purpose: "logo",
        body: fileBody,
        mimeType,
      });
      if (logoPath && logoPath !== path) await removeFile(logoPath);
      setLogoPath(path);
      await workspaceSettings.mutateAsync({
        address,
        bankAccountName,
        bankAccountNumber,
        bankName,
        brandAccentHex,
        contactEmail,
        defaultHourlyRate,
        defaultInvoiceRounding,
        defaultPaymentInstructions,
        defaultTimeRounding,
        gstNumber,
        gstRegistered,
        irdNumber,
        invoicePrefix,
        legalName,
        logoPath: path,
        name: workspaceName,
        nzbn,
        phone,
        taxSetAsidePercentage: workspaceSubscription.canUseConfigurableSetAside
          ? taxSetAsidePercentage
          : String(workspace.data?.tax_set_aside_percentage ?? 0),
      });
      Alert.alert("Logo updated");
    } catch (error) {
      Alert.alert(
        "Logo upload failed",
        error instanceof Error ? error.message : "Unable to upload logo.",
      );
    } finally {
      setLogoUploading(false);
    }
  }

  async function removeLogo() {
    if (!workspace.data?.id) return;
    setLogoUploading(true);
    try {
      if (logoPath) {
        await removeFile(logoPath);
      }
      setLogoPath(null);
      await workspaceSettings.mutateAsync({
        address,
        bankAccountName,
        bankAccountNumber,
        bankName,
        brandAccentHex,
        contactEmail,
        defaultHourlyRate,
        defaultInvoiceRounding,
        defaultPaymentInstructions,
        defaultTimeRounding,
        gstNumber,
        gstRegistered,
        irdNumber,
        invoicePrefix,
        legalName,
        logoPath: "",
        name: workspaceName,
        nzbn,
        phone,
        taxSetAsidePercentage: workspaceSubscription.canUseConfigurableSetAside
          ? taxSetAsidePercentage
          : String(workspace.data?.tax_set_aside_percentage ?? 0),
      });
      Alert.alert("Logo removed");
    } catch (error) {
      Alert.alert(
        "Could not remove logo",
        error instanceof Error ? error.message : "Unable to remove logo.",
      );
    } finally {
      setLogoUploading(false);
    }
  }
  const submitPassword = passwordForm.handleSubmit(async (values) => {
    try {
      await changePassword(values);
      passwordForm.reset();
      Alert.alert("Password updated");
    } catch (error) {
      passwordForm.setError("root", {
        message:
          error instanceof Error ? error.message : "Unable to update password.",
      });
    }
  });
  const submitSetPassword = setPasswordForm.handleSubmit(
    async ({ password }) => {
      try {
        await setPassword({ password });
        setPasswordForm.reset();
        Alert.alert("Password added");
      } catch (error) {
        setPasswordForm.setError("root", {
          message:
            error instanceof Error ? error.message : "Unable to add password.",
        });
      }
    },
  );
  const planLabel = subscription.hasPaidAccess ? "MintStint Pro" : "Inactive";
  const displayPackages = (
    checkoutIntent
      ? [...purchases.packages].sort(
          (a, b) =>
            checkoutSortValue(a, checkoutIntent) -
            checkoutSortValue(b, checkoutIntent),
        )
      : purchases.packages
  ).filter((pkg) => pkg.billingPeriod !== "annual");
  const dismissCheckoutIntent = () => {
    clearPendingCheckoutIntent();
    router.replace("/settings" as never);
  };
  const manageSubscription = () => {
    Alert.alert(
      "Manage subscription?",
      `MintStint Pro is ${PRO_PRICE_PER_MONTH}. You can manage or cancel your plan from subscription settings.`,
      [
        { text: "Not now", style: "cancel" },
        {
          text: "Open subscription settings",
          onPress: async () => {
            try {
              if (purchases.supportsCustomerCenter) {
                await purchases.presentCustomerCenter();
                await refresh();
                return;
              }
              const url = await purchases.getManagementUrl();
              if (!url) {
                Alert.alert(
                  "No subscription link found",
                  "RevenueCat did not return a management URL for this account. Restore purchases, then try again.",
                );
                return;
              }
              await Linking.openURL(url);
            } catch (error) {
              Alert.alert(
                "Unable to open subscription settings",
                error instanceof Error ? error.message : "Please try again.",
              );
            }
          },
        },
      ],
    );
  };
  const canManageWorkspace = Boolean(
    workspace.data?.owner_id === user?.id ||
    workspaceMembership.data?.role === "admin" ||
    workspaceMembership.data?.role === "owner",
  );
  const canManagePersonalRates = Boolean(
    canManageWorkspace || workspaceMembership.data?.role === "member",
  );
  const clientRecords = clients.data ?? [];
  const projectRecords = projects.data ?? [];
  const personalRates = userRates.rates.filter(
    (rate) => rate.user_id === user?.id,
  );
  const rateClientOptions = [
    { label: "All clients", value: "" },
    ...clientRecords.map((client) => ({
      label: client.name,
      value: client.id,
    })),
  ];
  const rateProjectOptions = [
    { label: "All projects", value: "" },
    ...projectRecords
      .filter(
        (project) => !newRateClientId || project.client_id === newRateClientId,
      )
      .map((project) => ({ label: project.name, value: project.id })),
  ];
  const projectStatusError =
    projectStatuses.error ??
    projectStatuses.create.error ??
    projectStatuses.update.error ??
    projectStatuses.remove.error;
  const rateError =
    userRates.error ??
    userRates.create.error ??
    userRates.update.error ??
    userRates.remove.error;

  const addProjectStatus = async () => {
    if (!newStatusName.trim()) return;
    try {
      await projectStatuses.create.mutateAsync({
        name: newStatusName,
        color: newStatusColour,
        state: newStatusState,
      });
      setNewStatusName("");
      setNewStatusColour("#0F766E");
      setNewStatusState("active");
    } catch {
      // The mutation error is rendered with the workflow controls.
    }
  };

  const confirmProjectStatusRemoval = (status: ProjectStatus) => {
    Alert.alert(
      `Remove ${status.name}?`,
      "Only unused statuses can be removed. Existing project records will not be changed.",
      [
        { text: "Cancel", style: "cancel" },
        {
          text: "Remove",
          style: "destructive",
          onPress: () => projectStatuses.remove.mutate(status.id),
        },
      ],
    );
  };

  const addRatePreset = async () => {
    if (!newRateName.trim() || !newRateHourly.trim()) return;
    try {
      await userRates.create.mutateAsync({
        clientId: newRateClientId,
        description: newRateDescription,
        effectiveFrom: newRateEffectiveFrom,
        hourlyRate: newRateHourly,
        name: newRateName,
        projectId: newRateProjectId,
      });
      setNewRateName("");
      setNewRateDescription("");
      setNewRateHourly("");
      setNewRateEffectiveFrom(new Date().toISOString().slice(0, 10));
      setNewRateClientId("");
      setNewRateProjectId("");
    } catch {
      // The mutation error is rendered with the rate controls.
    }
  };

  const selectNewRateProject = (value: string) => {
    setNewRateProjectId(value);
    const project = projectRecords.find((candidate) => candidate.id === value);
    if (project?.client_id) setNewRateClientId(project.client_id);
  };

  const confirmRateRemoval = (rate: RatePreset) => {
    Alert.alert(
      `Remove ${rate.name}?`,
      "Existing time entries keep their captured rate. This only removes the preset from future rate matching.",
      [
        { text: "Cancel", style: "cancel" },
        {
          text: "Remove",
          style: "destructive",
          onPress: () => userRates.remove.mutate(rate.id),
        },
      ],
    );
  };

  return (
    <ProtectedScreen title="Settings">
      <Card gap="sm">
        <AppText weight="semibold">Appearance</AppText>
        <Row between>
          <AppText size="xs" tone="muted">
            Light, dark, or match your device
          </AppText>
          <ThemeToggle />
        </Row>
      </Card>

      <Card gap="sm">
        <AppText weight="semibold">Subscription</AppText>
        <AppText size="xs">
          Current plan: {planLabel}
          {!subscription.hasPaidAccess
            ? ` — Subscribe to MintStint Pro for ${PRO_PRICE_PER_MONTH} to edit and run automations.`
            : null}
          {purchases.hasMintStintPro
            ? " — RevenueCat entitlement MintStint Pro is active."
            : null}
        </AppText>
        {checkoutIntent ? (
          <Stack gap="xs">
            <AppText weight="semibold">
              Continue with {signupPlanLabel(checkoutIntent.plan)}{" "}
              {checkoutIntent.billing}
            </AppText>
            <AppText size="xs">
              {`Complete purchase below to activate MintStint Pro at ${PRO_PRICE_PER_MONTH}.`}
            </AppText>
            <Button
              variant="outline"
              compact
              align="start"
              onPress={dismissCheckoutIntent}
            >
              <ButtonText>Choose later</ButtonText>
            </Button>
          </Stack>
        ) : null}
        {purchases.error ? (
          <AppText tone="danger" size="xs">
            {purchases.error}
          </AppText>
        ) : null}
        <Stack gap="sm">
          {purchases.supportsNativePaywall ? (
            <Button
              variant="primary"
              onPress={async () => {
                try {
                  const result = await purchases.presentPaywall();
                  if (result === "purchased" || result === "restored") {
                    clearPendingCheckoutIntent();
                    await refresh();
                    router.replace("/settings" as never);
                    Alert.alert(
                      "Purchase complete",
                      "Your MintStint Pro subscription is updating.",
                    );
                  }
                } catch (error) {
                  Alert.alert(
                    "Purchase failed",
                    error instanceof Error
                      ? error.message
                      : "Please try again.",
                  );
                }
              }}
              disabled={purchases.purchasing || !purchases.configured}
            >
              <ButtonText>
                {purchases.purchasing
                  ? "Opening..."
                  : checkoutIntent
                    ? `Continue with ${signupPlanLabel(checkoutIntent.plan)} ${checkoutIntent.billing}`
                    : "View MintStint Pro plans"}
              </ButtonText>
            </Button>
          ) : (
            displayPackages.map((pkg) => {
              const highlighted = checkoutIntent
                ? pkg.plan === checkoutIntent.plan &&
                  pkg.billingPeriod === checkoutIntent.billing
                : pkg.billingPeriod === "monthly";
              return (
                <Button
                  key={pkg.identifier}
                  variant={highlighted ? "primary" : "outline"}
                  onPress={async () => {
                    try {
                      await purchases.purchase(pkg);
                      clearPendingCheckoutIntent();
                      await refresh();
                      router.replace("/settings" as never);
                      Alert.alert(
                        "Purchase complete",
                        "Your MintStint Pro subscription is updating.",
                      );
                    } catch (error) {
                      if (
                        error &&
                        typeof error === "object" &&
                        "userCancelled" in error &&
                        (error as { userCancelled?: boolean }).userCancelled
                      ) {
                        return;
                      }
                      Alert.alert(
                        "Purchase failed",
                        error instanceof Error
                          ? error.message
                          : "Please try again.",
                      );
                    }
                  }}
                  disabled={purchases.purchasing}
                >
                  <ButtonText>
                    {checkoutIntent &&
                    pkg.billingPeriod === checkoutIntent.billing
                      ? `Continue with ${purchasePackageLabel(pkg)}`
                      : purchasePackageLabel(pkg)}
                  </ButtonText>
                </Button>
              );
            })
          )}
        </Stack>
        <Row>
          <Button
            variant="outline"
            onPress={async () => {
              try {
                await purchases.restore();
                await refresh();
                Alert.alert("Purchases restored");
              } catch (error) {
                Alert.alert(
                  "Restore failed",
                  error instanceof Error ? error.message : "Please try again.",
                );
              }
            }}
            disabled={!purchases.configured || purchases.loading}
          >
            <ButtonText>
              {purchases.loading ? "Loading plans..." : "Restore purchases"}
            </ButtonText>
          </Button>
          <Button
            variant="outline"
            onPress={manageSubscription}
            disabled={!purchases.configured || purchases.managing}
          >
            <ButtonText>
              {purchases.managing ? "Opening..." : "Manage or cancel"}
            </ButtonText>
          </Button>
        </Row>
        {!purchases.loading && purchases.packages.length === 0 ? (
          <AppText size="xs">
            RevenueCat plans are not configured for this environment.
          </AppText>
        ) : null}
      </Card>

      <Card gap="sm">
        <AppText weight="semibold">Automation</AppText>
        <View style={stylesheet.statusGrid}>
          <View style={stylesheet.statusItem}>
            <AppText size="xs" weight="semibold">
              Recurring invoices
            </AppText>
            <AppText size="xs">
              {workspaceSubscription.canUseRecurringInvoices
                ? "Ready"
                : "Subscription needed"}
            </AppText>
          </View>
          <View style={stylesheet.statusItem}>
            <AppText size="xs" weight="semibold">
              Invoice reminders
            </AppText>
            <AppText size="xs">
              {workspaceSubscription.canUseAutomatedReminders
                ? "Ready"
                : "Subscription needed"}
            </AppText>
          </View>
          <View style={stylesheet.statusItem}>
            <AppText size="xs" weight="semibold">
              Email delivery
            </AppText>
            <AppText size="xs">Resend</AppText>
          </View>
        </View>
      </Card>

      <Card gap="sm">
        <AppText weight="semibold">Workspace and NZ tax settings</AppText>
        <AppText size="xs">
          These settings feed invoices, default rates, and set-aside estimates.
          Set-aside amounts are planning estimates, not tax advice.
        </AppText>
        {!canManageWorkspace && !workspaceMembership.isLoading ? (
          <AppText size="xs">
            Workspace owners and admins can change business and invoice
            defaults.
          </AppText>
        ) : null}
        <View style={stylesheet.formGrid}>
          <View style={stylesheet.field}>
            <AppText size="xs" weight="semibold">
              Workspace name
            </AppText>
            <TextField
              value={workspaceName}
              placeholder="MintStint Workspace"
              editable={canManageWorkspace}
              muted={!canManageWorkspace}
              onChangeText={setWorkspaceName}
            />
          </View>
          <View style={stylesheet.field}>
            <AppText size="xs" weight="semibold">
              Legal name
            </AppText>
            <TextField
              value={legalName}
              placeholder="Your trading or company name"
              editable={canManageWorkspace}
              muted={!canManageWorkspace}
              onChangeText={setLegalName}
            />
          </View>
          <View style={stylesheet.field}>
            <AppText size="xs" weight="semibold">
              Business email
            </AppText>
            <TextField
              value={contactEmail}
              placeholder="accounts@example.co.nz"
              keyboardType="email-address"
              autoCapitalize="none"
              editable={canManageWorkspace}
              muted={!canManageWorkspace}
              onChangeText={setContactEmail}
            />
          </View>
          <View style={stylesheet.field}>
            <AppText size="xs" weight="semibold">
              Business phone
            </AppText>
            <TextField
              value={phone}
              placeholder="+64 21 000 000"
              keyboardType="phone-pad"
              editable={canManageWorkspace}
              muted={!canManageWorkspace}
              onChangeText={setPhone}
            />
          </View>
          <View style={stylesheet.fieldWide}>
            <AppText size="xs" weight="semibold">
              Business address
            </AppText>
            <TextField
              value={address}
              placeholder="Street, suburb, city, postcode"
              multiline
              editable={canManageWorkspace}
              muted={!canManageWorkspace}
              style={stylesheet.multilineField}
              onChangeText={setAddress}
            />
          </View>
          <View style={stylesheet.field}>
            <AppText size="xs" weight="semibold">
              NZBN
            </AppText>
            <TextField
              value={nzbn}
              placeholder="9429000000000"
              keyboardType="numeric"
              editable={canManageWorkspace}
              muted={!canManageWorkspace}
              onChangeText={setNzbn}
            />
          </View>
          <View style={stylesheet.field}>
            <AppText size="xs" weight="semibold">
              IRD number
            </AppText>
            <TextField
              value={irdNumber}
              placeholder="Optional"
              keyboardType="numeric"
              editable={canManageWorkspace}
              muted={!canManageWorkspace}
              onChangeText={setIrdNumber}
            />
          </View>
          <View style={stylesheet.field}>
            <AppText size="xs" weight="semibold">
              GST number
            </AppText>
            <TextField
              value={gstNumber}
              placeholder="123-456-789"
              editable={canManageWorkspace}
              muted={!canManageWorkspace}
              onChangeText={setGstNumber}
            />
          </View>
          <View style={stylesheet.field}>
            <AppText size="xs" weight="semibold">
              Invoice prefix
            </AppText>
            <TextField
              value={invoicePrefix}
              placeholder="MS"
              autoCapitalize="characters"
              editable={canManageWorkspace}
              muted={!canManageWorkspace}
              onChangeText={setInvoicePrefix}
            />
          </View>
          <View style={stylesheet.field}>
            <AppText size="xs" weight="semibold">
              Default hourly rate
            </AppText>
            <TextField
              value={defaultHourlyRate}
              placeholder="120"
              keyboardType="numeric"
              editable={canManageWorkspace}
              muted={!canManageWorkspace}
              onChangeText={setDefaultHourlyRate}
            />
          </View>
          <View style={stylesheet.field}>
            <AppText size="xs" weight="semibold">
              Invoice total rounding
            </AppText>
            <SearchableSelect
              title="Choose invoice rounding"
              value={defaultInvoiceRounding}
              options={INVOICE_ROUNDING_OPTIONS}
              disabled={!canManageWorkspace}
              onValueChange={(value) =>
                setDefaultInvoiceRounding(
                  value === "nearest_0_05" ? value : "none",
                )
              }
            />
          </View>
          <View style={stylesheet.field}>
            <AppText size="xs" weight="semibold">
              Default time rounding
            </AppText>
            <SearchableSelect
              title="Choose time rounding"
              value={defaultTimeRounding}
              options={TIME_ROUNDING_OPTIONS}
              disabled={!canManageWorkspace}
              onValueChange={(value) =>
                setDefaultTimeRounding(
                  TIME_ROUNDING_OPTIONS.some((option) => option.value === value)
                    ? (value as TimeRoundingMethod)
                    : "none",
                )
              }
            />
          </View>
          <View style={stylesheet.field}>
            <AppText size="xs" weight="semibold">
              Brand accent hex
            </AppText>
            <TextField
              value={brandAccentHex}
              placeholder="#0F766E"
              autoCapitalize="characters"
              editable={canManageWorkspace}
              muted={!canManageWorkspace}
              onChangeText={setBrandAccentHex}
            />
          </View>
          <View style={stylesheet.fieldWide}>
            <AppText size="xs" weight="semibold">
              Workspace logo
            </AppText>
            {logoPath && workspaceLogoPublicUrl(logoPath) ? (
              <Image
                source={{ uri: workspaceLogoPublicUrl(logoPath)! }}
                style={stylesheet.logoPreview}
                resizeMode="contain"
                accessibilityLabel="Workspace logo preview"
              />
            ) : (
              <AppText size="xs">No logo uploaded.</AppText>
            )}
            <Row gap="sm" wrap>
              <Button
                compact
                variant="outline"
                disabled={!canManageWorkspace || logoUploading}
                onPress={() => void pickAndUploadLogo()}
              >
                <ButtonText>
                  {logoUploading ? "Uploading..." : "Upload logo"}
                </ButtonText>
              </Button>
              {logoPath ? (
                <Button
                  compact
                  variant="outline"
                  disabled={!canManageWorkspace || logoUploading}
                  onPress={() => void removeLogo()}
                >
                  <ButtonText>Remove logo</ButtonText>
                </Button>
              ) : null}
            </Row>
            <AppText size="xs">
              JPEG, PNG, or WebP up to 2 MB. Used on branded invoices and
              quotes.
            </AppText>
          </View>
          <View style={stylesheet.field}>
            <AppText size="xs" weight="semibold">
              Bank
            </AppText>
            <TextField
              value={bankName}
              placeholder="Your bank"
              editable={canManageWorkspace}
              muted={!canManageWorkspace}
              onChangeText={setBankName}
            />
          </View>
          <View style={stylesheet.field}>
            <AppText size="xs" weight="semibold">
              Account name
            </AppText>
            <TextField
              value={bankAccountName}
              placeholder="Business or account name"
              editable={canManageWorkspace}
              muted={!canManageWorkspace}
              onChangeText={setBankAccountName}
            />
          </View>
          <View style={stylesheet.field}>
            <AppText size="xs" weight="semibold">
              Account number
            </AppText>
            <TextField
              value={bankAccountNumber}
              placeholder="00-0000-0000000-00"
              keyboardType="numeric"
              editable={canManageWorkspace}
              muted={!canManageWorkspace}
              onChangeText={setBankAccountNumber}
            />
          </View>
          <View style={stylesheet.fieldWide}>
            <AppText size="xs" weight="semibold">
              Default payment instructions
            </AppText>
            <TextField
              value={defaultPaymentInstructions}
              placeholder="Payment terms or reference instructions added to new invoices"
              multiline
              editable={canManageWorkspace}
              muted={!canManageWorkspace}
              style={stylesheet.multilineField}
              onChangeText={setDefaultPaymentInstructions}
            />
          </View>
          <View style={stylesheet.field}>
            <AppText size="xs" weight="semibold">
              Set-aside estimate %
            </AppText>
            <TextField
              value={taxSetAsidePercentage}
              placeholder="25"
              editable={
                canManageWorkspace &&
                workspaceSubscription.canUseConfigurableSetAside
              }
              keyboardType="numeric"
              muted={
                !canManageWorkspace ||
                !workspaceSubscription.canUseConfigurableSetAside
              }
              onChangeText={setTaxSetAsidePercentage}
            />
            {!workspaceSubscription.canUseConfigurableSetAside ? (
              <AppText size="xs">
                A subscription unlocks configurable set-aside estimates. The
                current estimate remains unchanged.
              </AppText>
            ) : null}
          </View>
          <View style={stylesheet.field}>
            <AppText size="xs" weight="semibold">
              Idle nudge (minutes)
            </AppText>
            <TextField
              accessibilityLabel="Idle nudge minutes"
              value={idleNudgeMinutes}
              placeholder="15"
              keyboardType="numeric"
              editable={canManageWorkspace}
              muted={!canManageWorkspace}
              onChangeText={setIdleNudgeMinutes}
            />
            <AppText size="xs">
              Ask &quot;Still tracking?&quot; after this many idle minutes while
              a timer runs. Use 0 to disable.
            </AppText>
          </View>
          <View style={stylesheet.field}>
            <AppText size="xs" weight="semibold">
              Focus reminder (minutes)
            </AppText>
            <TextField
              accessibilityLabel="Focus reminder minutes"
              value={focusReminderMinutes}
              placeholder="0"
              keyboardType="numeric"
              editable={canManageWorkspace}
              muted={!canManageWorkspace}
              onChangeText={setFocusReminderMinutes}
            />
            <AppText size="xs">
              Periodic check-in while a timer runs. Use 0 to disable.
            </AppText>
          </View>
        </View>
        <View style={stylesheet.formGrid}>
          <View style={stylesheet.fieldWide}>
            <AppText size="xs" weight="semibold">
              Invoice email subject
            </AppText>
            <TextField
              accessibilityLabel="Invoice email subject template"
              value={invoiceEmailSubjectTemplate}
              placeholder="Invoice {{invoice_number}} from {{workspace_name}}"
              editable={canManageWorkspace}
              muted={!canManageWorkspace}
              onChangeText={setInvoiceEmailSubjectTemplate}
            />
          </View>
          <View style={stylesheet.fieldWide}>
            <AppText size="xs" weight="semibold">
              Invoice email body
            </AppText>
            <TextField
              accessibilityLabel="Invoice email body template"
              value={invoiceEmailBodyTemplate}
              placeholder="Hi {{client_name}}, ..."
              multiline
              editable={canManageWorkspace}
              muted={!canManageWorkspace}
              style={stylesheet.multilineField}
              onChangeText={setInvoiceEmailBodyTemplate}
            />
            <AppText size="xs">
              Placeholders: {"{{invoice_number}}"} {"{{client_name}}"}{" "}
              {"{{workspace_name}}"} {"{{total}}"} {"{{public_url}}"}
            </AppText>
          </View>
          <View style={stylesheet.fieldWide}>
            <AppText size="xs" weight="semibold">
              Quote email subject
            </AppText>
            <TextField
              accessibilityLabel="Quote email subject template"
              value={quoteEmailSubjectTemplate}
              placeholder="{{workspace_name}} sent quote {{quote_number}}"
              editable={canManageWorkspace}
              muted={!canManageWorkspace}
              onChangeText={setQuoteEmailSubjectTemplate}
            />
          </View>
          <View style={stylesheet.fieldWide}>
            <AppText size="xs" weight="semibold">
              Quote email body
            </AppText>
            <TextField
              accessibilityLabel="Quote email body template"
              value={quoteEmailBodyTemplate}
              placeholder="Hi {{client_name}}, ..."
              multiline
              editable={canManageWorkspace}
              muted={!canManageWorkspace}
              style={stylesheet.multilineField}
              onChangeText={setQuoteEmailBodyTemplate}
            />
            <AppText size="xs">
              Placeholders: {"{{quote_number}}"} {"{{client_name}}"}{" "}
              {"{{workspace_name}}"} {"{{total}}"} {"{{public_url}}"}
            </AppText>
          </View>
        </View>
        <Row>
          <Button
            compact
            variant={gstRegistered ? "primary" : "outline"}
            disabled={!canManageWorkspace}
            onPress={() => setGstRegistered(true)}
          >
            <ButtonText>GST registered</ButtonText>
          </Button>
          <Button
            compact
            variant={!gstRegistered ? "primary" : "outline"}
            disabled={!canManageWorkspace}
            onPress={() => setGstRegistered(false)}
          >
            <ButtonText>Not GST registered</ButtonText>
          </Button>
        </Row>
        {(workspace.error ?? workspaceSettings.error) ? (
          <AppText tone="danger" size="xs">
            {(workspace.error ?? workspaceSettings.error) instanceof Error
              ? (workspace.error ?? workspaceSettings.error)?.message
              : "Unable to load workspace settings."}
          </AppText>
        ) : null}
        <Button
          onPress={() => void saveWorkspaceSettings()}
          disabled={
            workspace.isLoading ||
            workspaceSettings.isPending ||
            !canManageWorkspace ||
            !workspaceName.trim()
          }
        >
          <ButtonText>
            {workspaceSettings.isPending ? "Saving..." : "Save workspace"}
          </ButtonText>
        </Button>
      </Card>

      <Card gap="md">
        <Stack gap="xs">
          <AppText weight="semibold">Project workflow</AppText>
          <AppText size="xs">
            Shape the stages used across project lists and project details.
            Behaviour controls whether a stage counts as active, paused,
            completed, or archived.
          </AppText>
          {!canManageWorkspace && !workspaceMembership.isLoading ? (
            <AppText size="xs">
              Workspace owners and admins can change these stages.
            </AppText>
          ) : null}
        </Stack>

        {canManageWorkspace ? (
          <View style={stylesheet.newStatusPanel}>
            <View style={stylesheet.statusNameField}>
              <AppText size="xs" weight="semibold">
                New status
              </AppText>
              <TextField
                value={newStatusName}
                placeholder="Awaiting approval"
                onChangeText={setNewStatusName}
              />
            </View>
            <View style={stylesheet.statusStateField}>
              <AppText size="xs" weight="semibold">
                Behaviour
              </AppText>
              <SegmentedControl
                accessibilityLabel="New project status behaviour"
                options={projectStateOptions}
                value={newStatusState}
                onChange={setNewStatusState}
              />
            </View>
            <View style={stylesheet.statusColourField}>
              <AppText size="xs" weight="semibold">
                Colour
              </AppText>
              <StatusColourPicker
                value={newStatusColour}
                onChange={setNewStatusColour}
              />
            </View>
            <Button
              compact
              align="start"
              iconName="plus"
              disabled={
                !newStatusName.trim() || projectStatuses.create.isPending
              }
              onPress={() => void addProjectStatus()}
            >
              <ButtonText>
                {projectStatuses.create.isPending ? "Adding..." : "Add status"}
              </ButtonText>
            </Button>
          </View>
        ) : null}

        {projectStatuses.isLoading ? (
          <AppText size="xs">Loading project workflow...</AppText>
        ) : (
          <Stack gap="sm">
            {(projectStatuses.data ?? []).map((status) => (
              <ProjectStatusEditor
                key={status.id}
                status={status}
                disabled={
                  !canManageWorkspace ||
                  projectStatuses.update.isPending ||
                  projectStatuses.remove.isPending
                }
                onSave={(input) => projectStatuses.update.mutateAsync(input)}
                onRemove={confirmProjectStatusRemoval}
              />
            ))}
          </Stack>
        )}
        {projectStatusError ? (
          <AppText size="xs" tone="danger">
            {projectStatusError instanceof Error
              ? projectStatusError.message
              : "Unable to update project workflow."}
          </AppText>
        ) : null}
      </Card>

      <Card gap="md">
        <Stack gap="xs">
          <AppText weight="semibold">Rate presets</AppText>
          <AppText size="xs">
            Named rates apply to your future billable time. Project-scoped rates
            take priority over client rates, followed by general presets,
            project defaults, and the workspace default.
          </AppText>
          {!canManagePersonalRates && !workspaceMembership.isLoading ? (
            <AppText size="xs">
              Viewers can inspect workspace data but cannot manage rate presets.
            </AppText>
          ) : null}
        </Stack>

        {canManagePersonalRates ? (
          <View style={stylesheet.newRatePanel}>
            <View style={stylesheet.rateNameField}>
              <AppText size="xs" weight="semibold">
                Name
              </AppText>
              <TextField
                value={newRateName}
                placeholder="Consulting"
                onChangeText={setNewRateName}
              />
            </View>
            <View style={stylesheet.rateField}>
              <AppText size="xs" weight="semibold">
                Hourly rate
              </AppText>
              <TextField
                value={newRateHourly}
                placeholder="140"
                keyboardType="numeric"
                onChangeText={setNewRateHourly}
              />
            </View>
            <View style={stylesheet.rateField}>
              <AppText size="xs" weight="semibold">
                Effective from
              </AppText>
              <TextField
                value={newRateEffectiveFrom}
                placeholder="2026-07-01"
                onChangeText={setNewRateEffectiveFrom}
              />
            </View>
            <View style={stylesheet.rateScopeField}>
              <AppText size="xs" weight="semibold">
                Client scope
              </AppText>
              <SearchableSelect
                title="Choose rate client"
                value={newRateClientId}
                options={rateClientOptions}
                onValueChange={(value) => {
                  setNewRateClientId(value);
                  setNewRateProjectId("");
                }}
              />
            </View>
            <View style={stylesheet.rateScopeField}>
              <AppText size="xs" weight="semibold">
                Project scope
              </AppText>
              <SearchableSelect
                title="Choose rate project"
                value={newRateProjectId}
                options={rateProjectOptions}
                onValueChange={selectNewRateProject}
              />
            </View>
            <View style={stylesheet.rateDescriptionField}>
              <AppText size="xs" weight="semibold">
                Description
              </AppText>
              <TextField
                value={newRateDescription}
                placeholder="When this rate should apply"
                onChangeText={setNewRateDescription}
              />
            </View>
            <Button
              compact
              align="start"
              iconName="plus"
              disabled={
                !newRateName.trim() ||
                !newRateHourly.trim() ||
                userRates.create.isPending
              }
              onPress={() => void addRatePreset()}
            >
              <ButtonText>
                {userRates.create.isPending ? "Adding..." : "Add rate"}
              </ButtonText>
            </Button>
          </View>
        ) : null}

        {userRates.isLoading || clients.isLoading || projects.isLoading ? (
          <AppText size="xs">Loading rate presets...</AppText>
        ) : personalRates.length === 0 ? (
          <EmptyState title="No personal rate presets yet." />
        ) : (
          <Stack gap="sm">
            {personalRates.map((rate) => (
              <RatePresetEditor
                key={rate.id}
                rate={rate}
                clients={clientRecords}
                projects={projectRecords}
                disabled={
                  !canManagePersonalRates ||
                  userRates.update.isPending ||
                  userRates.remove.isPending
                }
                onSave={(input) => userRates.update.mutateAsync(input)}
                onRemove={confirmRateRemoval}
              />
            ))}
          </Stack>
        )}
        {rateError ? (
          <AppText size="xs" tone="danger">
            {rateError instanceof Error
              ? rateError.message
              : "Unable to update rate presets."}
          </AppText>
        ) : null}
      </Card>

      <Card gap="sm">
        <AppText weight="semibold">Profile</AppText>
        <View style={stylesheet.field}>
          <AppText weight="medium">Email</AppText>
          <TextField value={user?.email ?? ""} editable={false} muted />
        </View>
        <FormField
          control={profileForm.control}
          name="firstName"
          label="First name"
        >
          {(field) => (
            <TextField {...field} value={String(field.value ?? "")} />
          )}
        </FormField>
        <FormField
          control={profileForm.control}
          name="lastName"
          label="Last name"
        >
          {(field) => (
            <TextField {...field} value={String(field.value ?? "")} />
          )}
        </FormField>
        <FormField
          control={profileForm.control}
          name="currency"
          label="Currency"
        >
          {(field) => (
            <SearchableSelect
              title="Choose currency"
              value={String(field.value ?? "")}
              options={CURRENCY_OPTIONS}
              onValueChange={field.onChangeText}
            />
          )}
        </FormField>
        <FormField
          control={profileForm.control}
          name="dateFormat"
          label="Date and number locale"
        >
          {(field) => (
            <SearchableSelect
              title="Choose locale"
              value={String(field.value ?? "")}
              options={LOCALE_OPTIONS}
              onValueChange={field.onChangeText}
            />
          )}
        </FormField>
        <FormField
          control={profileForm.control}
          name="timezone"
          label="Timezone"
        >
          {(field) => (
            <SearchableSelect
              title="Choose timezone"
              value={String(field.value ?? "")}
              options={TIMEZONE_OPTIONS}
              onValueChange={field.onChangeText}
            />
          )}
        </FormField>
        {profileForm.formState.errors.root?.message ? (
          <AppText tone="danger" size="xs">
            {profileForm.formState.errors.root.message}
          </AppText>
        ) : null}
        <Button
          onPress={() => void submitProfile()}
          disabled={updateProfilePending}
        >
          <ButtonText>
            {updateProfilePending ? "Saving..." : "Save profile"}
          </ButtonText>
        </Button>
      </Card>

      <Card gap="sm">
        <AppText weight="semibold">Password</AppText>
        {user?.hasPassword ? (
          <>
            <FormField
              control={passwordForm.control}
              name="currentPassword"
              label="Current password"
            >
              {(field) => (
                <PasswordField
                  {...field}
                  value={String(field.value ?? "")}
                  accessibilityLabel="Current password"
                />
              )}
            </FormField>
            <FormField
              control={passwordForm.control}
              name="newPassword"
              label="New password"
            >
              {(field) => (
                <PasswordField
                  {...field}
                  value={String(field.value ?? "")}
                  accessibilityLabel="New password"
                />
              )}
            </FormField>
            {passwordForm.formState.errors.root?.message ? (
              <AppText tone="danger" size="xs">
                {passwordForm.formState.errors.root.message}
              </AppText>
            ) : null}
            <Button
              onPress={() => void submitPassword()}
              disabled={changePasswordPending}
            >
              <ButtonText>
                {changePasswordPending ? "Updating..." : "Update password"}
              </ButtonText>
            </Button>
          </>
        ) : (
          <>
            <AppText size="xs">
              Add a password to sign in with email and password as well as your
              connected provider.
            </AppText>
            <FormField
              control={setPasswordForm.control}
              name="password"
              label="New password"
            >
              {(field) => (
                <PasswordField
                  {...field}
                  value={String(field.value ?? "")}
                  autoComplete="new-password"
                  accessibilityLabel="New password"
                />
              )}
            </FormField>
            <FormField
              control={setPasswordForm.control}
              name="confirmPassword"
              label="Confirm new password"
            >
              {(field) => (
                <PasswordField
                  {...field}
                  value={String(field.value ?? "")}
                  autoComplete="new-password"
                  accessibilityLabel="Confirm new password"
                />
              )}
            </FormField>
            {setPasswordForm.formState.errors.root?.message ? (
              <AppText tone="danger" size="xs">
                {setPasswordForm.formState.errors.root.message}
              </AppText>
            ) : null}
            <Button
              onPress={() => void submitSetPassword()}
              disabled={setPasswordPending}
            >
              <ButtonText>
                {setPasswordPending ? "Saving..." : "Set password"}
              </ButtonText>
            </Button>
          </>
        )}
      </Card>

      {user ? <TwoFactorCard /> : null}

      <Card gap="sm">
        <AppText weight="semibold">Data Privacy (GDPR)</AppText>
        <AppText size="xs">
          Export a copy of your MintStint account data.
        </AppText>
        {privacy.error ? (
          <AppText tone="danger" size="xs">
            {privacy.error.message}
          </AppText>
        ) : null}
        <Button
          variant="outline"
          align="start"
          onPress={async () => {
            const data = await privacy.exportAccount();
            if (!data) {
              Alert.alert("Export failed", "Unable to export data right now.");
              return;
            }
            const { saveJsonExport } = await import("~/utils/export-file");
            await saveJsonExport(data);
          }}
          disabled={privacy.exportPending}
        >
          <ButtonText>
            {privacy.exportPending ? "Preparing..." : "Export my data"}
          </ButtonText>
        </Button>
      </Card>

      <Card gap="sm">
        <SectionHeader
          title="About this app"
          description="Installed version and over-the-air updates."
        />
        <AppVersionPanel
          colors={{
            text: colors.foreground,
            muted: colors.mutedForeground,
            border: colors.border,
            accent: colors.primary,
            onAccent: colors.primaryForeground,
            danger: colors.danger,
          }}
        />
      </Card>

      {user ? (
        <DeleteAccountPanel
          privacy={privacy}
          email={user.email}
          summary="Permanently delete your account, owned workspaces, and MintStint records 30 days after you confirm. You will be removed from shared workspaces. You can cancel until then."
          scheduledLabel={
            privacy.scheduledForDeletionAt
              ? new Date(privacy.scheduledForDeletionAt).toLocaleString(
                  user.dateFormat ?? "en-NZ",
                  { timeZone: user.timezone ?? "Pacific/Auckland" },
                )
              : null
          }
        />
      ) : null}
    </ProtectedScreen>
  );
}
